import React from 'react';
import { useDropzone } from 'react-dropzone';
import { AlertTitle, AlertView, GradientTextAction, InputLabel, Poppins } from 'src/common';
import Button from 'src/components/form/Button';
import { ConsequencesSelect, EventsSelect, SourcesSelect } from 'src/components/select';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { Dropzone, DzoneWrap } from 'src/common/dropzone';
import { generateCsvTemplateLink } from './util';
import { MissingVariablesForm } from './MissingVariablesForm';
import { useScenarioCsvUpload } from './useScenarioCsvUpload';
import { DashedContainer } from 'src/common/DashedContainer';

interface FromCsvProps {
  assessmentId: string;
  onClose: () => void;
  setIsScvDirty: (isDirty: boolean) => void;
}

export const FromCsv: React.FC<FromCsvProps> = ({ assessmentId, onClose, setIsScvDirty }) => {
  const { state, handlers } = useScenarioCsvUpload({ assessmentId, setIsScvDirty });
  const {
    file,
    readyForUpload,
    error,
    isUploadSuccessful,
    validationErrors,
    validScenarios,
    formulaVariables,
    isUploading,
    csvVariables,
    manualVariables,
    missingVariableNames,
  } = state;
  const { onDrop, saveScenarios, resetUpload, handleVariablesLoaded, setManualVariables } = handlers;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
  });

  const csvTemplateLink = generateCsvTemplateLink();

  return (
    <DzoneWrap className="h-padding">
      {!isUploadSuccessful && (
        <Dropzone {...getRootProps()}>
          <div style={{ position: 'absolute', right: 8, top: 8 }}>
            <a
              href={csvTemplateLink}
              download="scenario_template.csv"
              style={{ textDecoration: 'none' }}
              onClick={() =>
                mpEvent(MPEvents.DownloadCSVTemplate, {
                  modal: 'Scenario add modal',
                  tags: ['SCENARIO'],
                })
              }
            >
              <GradientTextAction $underline weight={500} px={14}>
                Download CSV Template
              </GradientTextAction>
            </a>
          </div>
          <input {...getInputProps()} />
          {file ? (
            <Poppins px={14}>File selected: {file.name}</Poppins>
          ) : (
            <Poppins px={14} weight={500}>
              {isDragActive ? 'Drop the file here ...' : 'Drag and drop a file here, or click to select file'}
            </Poppins>
          )}
        </Dropzone>
      )}

      {missingVariableNames.length > 0 && (
        <MissingVariablesForm
          missingVariableNames={missingVariableNames}
          csvVariables={csvVariables}
          manualVariables={manualVariables}
          onManualVariablesChange={setManualVariables}
        />
      )}

      {isUploadSuccessful && <Poppins px={14}>File uploaded successfully!</Poppins>}

      {validationErrors.length > 0 && (
        <AlertView>
          {validationErrors.map((error) => (
            <AlertTitle key={JSON.stringify(error.errors)}>
              <div style={{ fontStyle: 'italic' }}>
                <Poppins color="error" px={16} weight={600}>
                  Error in row {error.rowIndex + 1}:
                </Poppins>
                <div />
                {error.errors.map((e, idx) => (
                  <Poppins px={14} color="error" css="display: block" key={idx}>
                    {`${e.message}`}
                  </Poppins>
                ))}
              </div>
            </AlertTitle>
          ))}
        </AlertView>
      )}

      {file && !isUploadSuccessful && (
        <DashedContainer>
          <Poppins px={14}>Scenarios ready: {validScenarios.length}</Poppins>
          <div />
          <Poppins px={14}>
            Variables found: {formulaVariables.filter((v) => v.found).length} | Missing: {missingVariableNames.length}
          </Poppins>
          {validationErrors.length > 0 && (
            <Poppins px={14} color="error">
              Errors: {validationErrors.length}
            </Poppins>
          )}
        </DashedContainer>
      )}

      {!isUploadSuccessful ? (
        <div className="buttons">
          <Button secondary onClick={onClose}>
            CANCEL
          </Button>
          <Button primary onClick={file ? saveScenarios : open} disabled={!!validationErrors.length || isUploading}>
            {file ? 'Upload' : 'Select File'}
          </Button>
        </div>
      ) : (
        <div className="buttons">
          <Button primary onClick={resetUpload}>
            Upload Another File
          </Button>
          <Button primary onClick={onClose}>
            Done
          </Button>
        </div>
      )}

      {!file && (
        <>
          <Poppins px={14} weight={500}>
            The valid values of Sources, Events and Consequences are listed in the 3 drop down lists below. Blank is
            also valid for each.
          </Poppins>

          <div className="grid-list">
            <div>
              <InputLabel>Sources</InputLabel>
              <SourcesSelect placeholder="Sources" menuPlacement="top" />
            </div>
            <div>
              <InputLabel>Events</InputLabel>
              <EventsSelect placeholder="Events" menuPlacement="top" />
            </div>
            <div>
              <InputLabel>Consequences</InputLabel>
              <ConsequencesSelect placeholder="Consequences" menuPlacement="top" />
            </div>
          </div>
        </>
      )}

      {error && <div className="error">{error}</div>}
    </DzoneWrap>
  );
};
